<template>
  <div class="profile">
    <router-view />
    <div class="content--main">
      <div v-if="!!authErrors.length" class="section__block">
        <div class="row justify-content-center">
          <div class="col-lg-6 col-md-8">
            <b-alert
              v-for="(error, index) in authErrors"
              :key="index"
              variant="danger"
              fade
              show
              dismissible
              @dismissed="unsetError"
            >
              {{ error }}
            </b-alert>
          </div>
        </div>
      </div>
      <div class="section__block-header">
        <ProfileImage :image="user.avatar" />
      </div>
      <div class="section__block my-30">
        <h4 class="text-center">
          {{ fullName }}
          <span v-if="user.displayName">({{ user.displayName }})</span>
        </h4>
      </div>
      <div class="section__block">
        <div class="row justify-content-center">
          <div class="col-lg-6 col-md-8">
            <div class="row">
              <div class="col-md-6 mb-20">
                <i class="fa fa-envelope"></i>
                &nbsp;
                <span>{{ user.email }}</span>
              </div>
              <div class="col-md-6 mb-20">
                <i class="fa fa-phone"></i>
                &nbsp;
                <span>{{ user.mobile || "&mdash;" }}</span>
              </div>
              <template v-for="meta in userMeta">
                <div class="col-12 mb-20" :key="meta.id" v-if="!!meta.value">
                  <span class="font-weight-bold">{{ meta.label }}: </span>
                  <template v-if="!!meta.value">
                    <a
                      v-if="meta.key === 'website'"
                      :href="meta.value"
                      target="_blank"
                      :style="{ color: primary }"
                    >
                      {{ meta.value }}
                    </a>
                    <span v-else>{{ meta.value }}</span>
                  </template>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="row justify-content-center mt-30 mb-50">
          <div class="col-lg-6 col-md-8 text-center">
            <ActionButton
              label="Edit profile"
              :theme="{ primary, secondary, danger, event }"
              class="mt-0 d-inline-block w-auto"
              @customEvent="handleEditProfileClick"
            />
          </div>
        </div>
      </div>
      <template
        v-if="isMainSocialLogin.length && isMainSocialLogin[0].value.status"
      >
        <div class="section__block">
          <div class="row justify-content-center">
            <div class="col-lg-6 col-md-8 text-center">
              <h4>Link your account with</h4>
            </div>
          </div>
        </div>
        <SocialAuth class="section__block text-center pb-100" />
      </template>
    </div>
  </div>
</template>

<script>
import SocialAuth from "@/modules/auth/components/SocialAuth";
import ProfileImage from "@/components/profile-image/ProfileImage";
import authTypes from "@/store/auth/types";
import ActionButton from "@/components/ActionButton";
import _ from "lodash";

export default {
  name: "AdminProfile",
  components: {
    SocialAuth,
    ActionButton,
    ProfileImage
  },
  data() {
    return {
      authErrors: this.$store.state.auth.errors,
      isMainSocialLogin: false
    };
  },
  computed: {
    fullName() {
      return this.user.firstName + " " + this.user.lastName;
    },
    user() {
      return this.$store.state.auth.user;
    },
    userMeta() {
      return _.uniqBy(
        _.filter(this.$store.state.auth.user.UserMeta, val => {
          return !val.key.includes("owner:company");
        }),
        "key"
      );
    },
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    options() {
      return this.$store.state.concierge.optionsList.data;
    }
  },
  async created() {
    await this.$store.dispatch("fetchOptions", "sociallogin");

    this.isMainSocialLogin = _.filter(this.options, [
      "key",
      "sociallogin.main"
    ]);
  },
  methods: {
    unsetError() {
      this.$store.dispatch(authTypes.AUTH_SET_ERRORS, []);
    },
    handleEditProfileClick() {
      this.$emit("showModal", { name: "edit" });
    }
  }
};
</script>

<style lang="scss" scoped>
.section {
  @media (min-width: 992px) {
  }
  @media (max-width: 991px) {
    .btn {
      float: right;
    }
    &__block {
      &-details {
        margin: 60px 0;
        p {
          &.header {
            text-align: center;
          }
        }
      }
    }
  }
  /deep/ &__block {
    color: #232323;
    &-header {
      display: flex;
      justify-content: center;
    }
    &-avatar {
      background-color: $gray-400;
      overflow: hidden;
      height: 160px;
      width: 160px;
      border-radius: 90px;
      img {
        height: 160px;
        width: 100%;
        object-fit: cover;
      }
    }
    &-details {
      i {
        &.fa {
          margin-right: 10px;
        }
      }
      p {
        font-size: 15px;
        line-height: 20px;
        margin-bottom: 10px;
        &.header {
          font-size: 18px;
          line-height: 24px;
          margin-bottom: 30px;
        }
      }
      .row {
        margin-bottom: 50px;
      }
    }
  }

  /deep/ &__header {
    margin: 60px 0 40px;
    font-size: 30px;
    line-height: 36px;
    font-weight: 700;
  }
  /deep/ &__content {
    width: 100%;
  }
}

.profile {
  padding: 0 15px;
  font-family: "Gotham";
  .content--main {
    height: calc(100vh - 181px);
    overflow-x: hidden;
    overflow-y: auto;
    a {
      font-family: "Gotham Book";
    }
    h4 {
      color: $gray-900;
      font-family: "Gotham";
      font-size: $font-size-md;
      & > span {
        font-size: $font-size-md;
      }
    }
    span {
      color: $gray-900;
      font-family: "Gotham Book";
      font-size: $font-size-sm;
      &.font-weight-bold {
        font-family: "Gotham";
      }
    }
  }
}
</style>
